import React, { useState, useEffect, useCallback } from 'react';
import { apiGet, apiPost, apiPut, apiDelete } from '../utils/api';
import { Contest, Poll } from '../types';

const TelegramContests: React.FC = () => {
  const USER_COL_WIDTH = 200;
  const POLL_COL_WIDTH = 35;
  const TOTAL_COL_WIDTH = 48;
  const [contests, setContests] = useState<Contest[]>([]);
  const [polls, setPolls] = useState<Poll[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingPolls, setLoadingPolls] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedContest, setSelectedContest] = useState<Contest | null>(null);
  const [showAddPollModal, setShowAddPollModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportData, setReportData] = useState<any>(null);
  const [loadingReport, setLoadingReport] = useState(false);
  
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    pages: 0
  });
  
  const [filters, setFilters] = useState({
    isActive: '',
    search: ''
  });

  const [newContest, setNewContest] = useState({
    name: '',
    description: ''
  });

  const fetchContests = useCallback(async (page: number = 1, limit: number = 50) => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        ...(filters.isActive && { isActive: filters.isActive }),
        ...(filters.search && { search: filters.search })
      });

      const response = await apiGet<{
        contests: Contest[];
        pagination: {
          page: number;
          limit: number;
          total: number;
          pages: number;
        };
      }>(`/contests?${params.toString()}`);

      setContests(response.contests);
      setPagination(response.pagination);
    } catch (error) {
      console.error('Ошибка загрузки конкурсов:', error);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const fetchPolls = useCallback(async () => {
    try {
      setLoadingPolls(true);
      const response = await apiGet<{
        polls: Poll[];
        pagination: any;
      }>('/polls?limit=1000');

      // Фильтруем только голосования без конкурса
      const pollsWithoutContest = response.polls.filter(poll => !poll.contestId);
      setPolls(pollsWithoutContest);
    } catch (error) {
      console.error('Ошибка загрузки голосований:', error);
    } finally {
      setLoadingPolls(false);
    }
  }, []);

  useEffect(() => {
    fetchContests(pagination.page, pagination.limit);
  }, [fetchContests, pagination.page, pagination.limit]);

  const handleCreateContest = async () => {
    try {
      if (!newContest.name.trim()) {
        alert('Необходимо указать название конкурса');
        return;
      }

      await apiPost('/contests', newContest);
      setNewContest({ name: '', description: '' });
      setShowCreateModal(false);
      fetchContests(pagination.page, pagination.limit);
    } catch (error: any) {
      console.error('Ошибка создания конкурса:', error);
      alert(error.response?.data?.error || 'Ошибка создания конкурса');
    }
  };

  const handleToggleStatus = async (contest: Contest) => {
    try {
      await apiPut(`/contests/${contest.id}`, {
        isActive: !contest.isActive
      });
      fetchContests(pagination.page, pagination.limit);
    } catch (error: any) {
      console.error('Ошибка обновления статуса:', error);
      alert(error.response?.data?.error || 'Ошибка обновления статуса');
    }
  };

  const handleDeleteContest = async (contest: Contest) => {
    if (!confirm(`Вы уверены, что хотите удалить конкурс "${contest.name}"?`)) {
      return;
    }

    try {
      await apiDelete(`/contests/${contest.id}`);
      fetchContests(pagination.page, pagination.limit);
    } catch (error: any) {
      console.error('Ошибка удаления конкурса:', error);
      alert(error.response?.data?.error || 'Ошибка удаления конкурса');
    }
  };

  const handleAddPoll = async (pollId: string) => {
    if (!selectedContest) return;

    try {
      await apiPost(`/contests/${selectedContest.id}/polls`, { pollId });
      setShowAddPollModal(false);
      fetchContests(pagination.page, pagination.limit);
      alert('Голосование добавлено в конкурс');
    } catch (error: any) {
      console.error('Ошибка добавления голосования:', error);
      alert(error.response?.data?.error || 'Ошибка добавления голосования');
    }
  };

  const handleRemovePoll = async (pollId: string) => {
    if (!selectedContest) return;

    if (!confirm('Вы уверены, что хотите удалить голосование из конкурса?')) {
      return;
    }

    try {
      await apiDelete(`/contests/${selectedContest.id}/polls/${pollId}`);
      fetchContests(pagination.page, pagination.limit);
      alert('Голосование удалено из конкурса');
    } catch (error: any) {
      console.error('Ошибка удаления голосования:', error);
      alert(error.response?.data?.error || 'Ошибка удаления голосования');
    }
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleString('ru-RU', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleShowReport = async (contest: Contest) => {
    try {
      setLoadingReport(true);
      setSelectedContest(contest);
      const report = await apiGet(`/polls/results/report?contestId=${contest.id}`);
      setReportData(report);
      setShowReportModal(true);
    } catch (error: any) {
      console.error('Ошибка загрузки отчета:', error);
      alert(error.response?.data?.error || 'Ошибка загрузки отчета');
    } finally {
      setLoadingReport(false);
    }
  };

  const closeReportModal = () => {
    setShowReportModal(false);
    setSelectedContest(null);
    setReportData(null);
  };

  return (
    <div className="p-6">
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">🏆 Конкурсы</h1>
          <p className="text-gray-600">Объединение голосований в конкурсы</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => fetchContests(pagination.page, pagination.limit)}
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300"
          >
            Обновить
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            + Создать конкурс
          </button>
        </div>
      </div>

      {/* Фильтры */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Поиск
            </label>
            <input
              type="text"
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              placeholder="Название или описание"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Статус
            </label>
            <select
              value={filters.isActive}
              onChange={(e) => setFilters({ ...filters, isActive: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Все</option>
              <option value="true">Активные</option>
              <option value="false">Неактивные</option>
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={() => fetchContests(1, pagination.limit)}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Применить фильтры
            </button>
          </div>
        </div>
      </div>

      {/* Список конкурсов */}
      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-2 text-gray-600">Загрузка конкурсов...</p>
        </div>
      ) : contests.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <p className="text-gray-500 text-lg">Конкурсы не найдены</p>
        </div>
      ) : (
        <div className="space-y-4">
          {contests.map((contest) => (
            <div key={contest.id} className="bg-white rounded-lg shadow p-6">
              <div className="flex justify-between items-start mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {contest.name}
                    </h3>
                    {contest.isActive ? (
                      <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded font-medium">
                        Активен
                      </span>
                    ) : (
                      <span className="px-2 py-1 bg-gray-100 text-gray-800 text-xs rounded font-medium">
                        Неактивен
                      </span>
                    )}
                  </div>
                  {contest.description && (
                    <p className="text-gray-600 mb-2">{contest.description}</p>
                  )}
                  <div className="flex items-center gap-4 text-sm text-gray-500">
                    <span>📊 Голосований: {contest._count?.polls || contest.polls?.length || 0}</span>
                    <span>👤 Создал: {contest.createdBy.name}</span>
                    <span>📅 {formatDate(contest.createdAt)}</span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleShowReport(contest)}
                    disabled={loadingReport}
                    className="px-3 py-1 bg-purple-600 text-white text-sm rounded hover:bg-purple-700 disabled:opacity-50"
                  >
                    📊 Отчет
                  </button>
                  <button
                    onClick={() => {
                      setSelectedContest(contest);
                      fetchPolls();
                      setShowAddPollModal(true);
                    }}
                    className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                  >
                    + Добавить голосование
                  </button>
                  <button
                    onClick={() => handleToggleStatus(contest)}
                    className={`px-3 py-1 text-sm rounded ${
                      contest.isActive
                        ? 'bg-yellow-600 text-white hover:bg-yellow-700'
                        : 'bg-green-600 text-white hover:bg-green-700'
                    }`}
                  >
                    {contest.isActive ? 'Деактивировать' : 'Активировать'}
                  </button>
                  <button
                    onClick={() => handleDeleteContest(contest)}
                    className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700"
                  >
                    Удалить
                  </button>
                </div>
              </div>

              {/* Список голосований в конкурсе */}
              {contest.polls && contest.polls.length > 0 && (
                <div className="mt-4 border-t pt-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-3">Голосования в конкурсе:</h4>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Голосование
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Кол-во голосов
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Статус
                          </th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Действия
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {contest.polls.map((poll) => {
                          // Определяем статус голосования
                          let statusText = '';
                          let statusClass = '';
                          
                          if (poll.isActive) {
                            if (poll.deadline && new Date(poll.deadline) < new Date()) {
                              statusText = 'Завершено';
                              statusClass = 'bg-gray-100 text-gray-800';
                            } else {
                              statusText = 'Активно';
                              statusClass = 'bg-green-100 text-green-800';
                            }
                          } else {
                            statusText = 'Неактивно';
                            statusClass = 'bg-gray-100 text-gray-800';
                          }
                          
                          return (
                            <tr key={poll.id} className="hover:bg-gray-50">
                              <td className="px-4 py-3">
                                <div>
                                  <span className="font-medium text-gray-900">{poll.question}</span>
                                  {poll.description && (
                                    <span className="text-sm text-gray-600 ml-2">- {poll.description}</span>
                                  )}
                                </div>
                              </td>
                              <td className="px-4 py-3">
                                <span className="text-sm font-medium text-gray-900">
                                  {poll._count?.votes || 0}
                                </span>
                              </td>
                              <td className="px-4 py-3">
                                <span className={`px-2 py-1 text-xs rounded font-medium ${statusClass}`}>
                                  {statusText}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-right">
                                <button
                                  onClick={() => handleRemovePoll(poll.id)}
                                  className="px-2 py-1 bg-red-500 text-white text-xs rounded hover:bg-red-600"
                                >
                                  Удалить
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Пагинация */}
      {pagination.pages > 1 && (
        <div className="mt-6 flex justify-center gap-2">
          <button
            onClick={() => fetchContests(pagination.page - 1, pagination.limit)}
            disabled={pagination.page === 1}
            className="px-4 py-2 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Назад
          </button>
          <span className="px-4 py-2 text-gray-700">
            Страница {pagination.page} из {pagination.pages}
          </span>
          <button
            onClick={() => fetchContests(pagination.page + 1, pagination.limit)}
            disabled={pagination.page === pagination.pages}
            className="px-4 py-2 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Вперед
          </button>
        </div>
      )}

      {/* Модальное окно создания конкурса */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">Создать конкурс</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Название *
                </label>
                <input
                  type="text"
                  value={newContest.name}
                  onChange={(e) => setNewContest({ ...newContest, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Название конкурса"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Описание
                </label>
                <textarea
                  value={newContest.description}
                  onChange={(e) => setNewContest({ ...newContest, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                  placeholder="Описание конкурса"
                />
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setNewContest({ name: '', description: '' });
                }}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
              >
                Отмена
              </button>
              <button
                onClick={handleCreateContest}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Создать
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно добавления голосования */}
      {showAddPollModal && selectedContest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">
              Добавить голосование в конкурс "{selectedContest.name}"
            </h2>
            {loadingPolls ? (
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <p className="mt-2 text-gray-600">Загрузка голосований...</p>
              </div>
            ) : polls.length === 0 ? (
              <p className="text-gray-500 text-center py-8">
                Нет доступных голосований (все уже в конкурсах)
              </p>
            ) : (
              <div className="space-y-2">
                {polls.map((poll) => (
                  <div
                    key={poll.id}
                    className="flex items-center justify-between p-3 border border-gray-200 rounded hover:bg-gray-50"
                  >
                    <div>
                      <div className="font-medium text-gray-900">{poll.question}</div>
                      {poll.description && (
                        <div className="text-sm text-gray-600">{poll.description}</div>
                      )}
                      <div className="text-xs text-gray-500 mt-1">
                        Голосов: {poll._count?.votes || 0}
                      </div>
                    </div>
                    <button
                      onClick={() => handleAddPoll(poll.id)}
                      className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                    >
                      Добавить
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="mt-6 flex justify-end">
              <button
                onClick={() => {
                  setShowAddPollModal(false);
                  setSelectedContest(null);
                }}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
              >
                Закрыть
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно отчета */}
      {showReportModal && reportData && selectedContest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-[95vw] w-full max-h-[90vh] overflow-auto">
            <div className="flex items-start justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900">
                {selectedContest.name}
              </h2>
              <button
                onClick={closeReportModal}
                className="px-3 py-1.5 border border-gray-300 rounded-md hover:bg-gray-50 text-sm"
              >
                Закрыть
              </button>
            </div>
            
            {reportData.users.length === 0 ? (
              <p className="text-gray-500 text-center py-8">Нет данных для отображения</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="divide-y divide-gray-200 border border-gray-300" style={{ tableLayout: 'fixed', width: `${USER_COL_WIDTH + (reportData.polls.length * POLL_COL_WIDTH) + TOTAL_COL_WIDTH}px` }}>
                  <colgroup>
                    <col style={{ width: `${USER_COL_WIDTH}px` }} />
                    {reportData.polls.map((poll: any) => (
                      <col key={`col-${poll.id}`} style={{ width: `${POLL_COL_WIDTH}px` }} />
                    ))}
                    <col style={{ width: `${TOTAL_COL_WIDTH}px` }} />
                  </colgroup>
                  <thead className="bg-gray-50 sticky top-0">
                    <tr style={{ height: '150px' }}>
                      <th className="text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-300 sticky left-0 bg-gray-50 z-10" style={{ width: `${USER_COL_WIDTH}px`, padding: '2px' }}>
                        Utilizatori
                      </th>
                      {reportData.polls.map((poll: any) => (
                        <th key={poll.id} className="text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-300" style={{ width: `${POLL_COL_WIDTH}px`, padding: '2px' }}>
                          <div className="flex items-center justify-center h-full">
                            <div style={{ writingMode: 'vertical-rl', textOrientation: 'mixed', transform: 'rotate(180deg)' }}>
                              <div className="font-bold">{poll.question}</div>
                              {poll.correctOptionText && (
                                <div className="text-xs text-green-600 font-semibold mt-2">
                                  {poll.correctOptionText}
                                </div>
                              )}
                            </div>
                          </div>
                        </th>
                      ))}
                      <th className="text-center text-xs font-bold text-gray-500 uppercase tracking-wider bg-yellow-50" style={{ width: `${TOTAL_COL_WIDTH}px`, padding: '2px' }}>
                        <div className="flex items-center justify-center h-full">
                          <div style={{ writingMode: 'vertical-rl', textOrientation: 'mixed', transform: 'rotate(180deg)' }}>
                            TOTAL
                          </div>
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {reportData.users
                      .sort((a: any, b: any) => {
                        // 1. Сначала по общему количеству очков (по убыванию)
                        if (b.totalPoints !== a.totalPoints) {
                          return b.totalPoints - a.totalPoints;
                        }
                        
                        // 2. Если очки одинаковые, сравниваем по порядку голосований (слева направо)
                        // Тот, у кого больше очков в первых голосованиях, выше
                        for (let i = 0; i < Math.max(a.pollResults.length, b.pollResults.length); i++) {
                          const aPoints = a.pollResults[i]?.points || 0;
                          const bPoints = b.pollResults[i]?.points || 0;
                          if (bPoints !== aPoints) {
                            return bPoints - aPoints;
                          }
                        }
                        
                        // 3. Если все одинаково, сортируем по алфавиту
                        return a.name.localeCompare(b.name);
                      })
                      .map((user: any, userIndex: number) => (
                        <tr key={user.id} className={userIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'} style={{ height: '20px' }}>
                          <td className="text-sm text-left font-medium text-gray-900 border-r border-gray-300 sticky left-0 bg-inherit z-10" style={{ padding: '2px' }}>
                            <div>
                              {user.type === 'player' ? (
                                <span className="text-blue-600">👤 {user.name}</span>
                              ) : (
                                <span className="text-gray-600">📱 {user.name}</span>
                              )}
                            </div>
                          </td>
                          {user.pollResults.map((result: any, pollIndex: number) => (
                            <td key={pollIndex} className="text-sm text-center border-r border-gray-300" style={{ padding: '0' }}>
                              {result ? (
                                result.points > 0 ? (
                                  <div className="text-yellow-600 font-bold text-sm" style={{ padding: '0', margin: '0', lineHeight: 1, whiteSpace: 'nowrap' }}>
                                    {result.points}
                                  </div>
                                ) : (
                                  <div className="text-green-600 text-2xl">
                                    ✓
                                  </div>
                                )
                              ) : (
                                <span className="text-gray-400">—</span>
                              )}
                            </td>
                          ))}
                          <td className="text-center font-bold text-yellow-600 bg-yellow-50" style={{ width: `${TOTAL_COL_WIDTH}px`, minWidth: `${TOTAL_COL_WIDTH}px`, maxWidth: `${TOTAL_COL_WIDTH}px`, padding: '2px', whiteSpace: 'nowrap' }}>
                            <div className="text-sm" style={{ lineHeight: 1 }}>
                              {user.totalPoints}
                            </div>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={closeReportModal}
                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Закрыть
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TelegramContests;

